import { Box, Button, CircularProgress, Tab, Tabs } from "@mui/material";
import { useCallback, useContext, useMemo, useState, useEffect } from "react";

import DataContext from "../../../../../../../../context/DataContext";
import useService from "../../../../../../../../hooks/useService";
import PermissionWrapper from "../../../../../../../global/PermissionWrapper";
import DrawerFilters from "../../../../../../../modules/drawer-filters";
import { useDrawerFiltersState } from "../../../../../../../modules/drawer-filters/hooks/useDrawerFiltersState";
import { transformFilters } from "../../../../../../Dashboard/FullPages/Reports/components/ReportsDetail/utils/dto";
import ReportTooltip from "../../ReportsList/components/ReportTooltip";

import ActionsButton from "./ActionsButton";
import ColumnSelectionTab from "./ColumnSelectionTab";
import DrawerFiltersListLocal from "./DrawerFiltersListLocal";
import FilterSearchLocal from "./FilterSearchLocal";
import SavedFiltersSection from "./SavedFiltersSection";
import Title from "./Title";

const Header = ({
  reportData,
  reportId,
  filters,
  updateFilter,
  bulkUpdateFilters,
  showActions = true,
  showFilters = true,
  showSearchBar = true,
  showTitle = true,
  availableColumns = [],
  drawerCloseSignal,
  setDrawerCloseSignal = () => {},
  onApplyStart = () => {},
}) => {
  const reportFiltersRepository = useService("repositories/tabular-report-filter-repository");
  const [reportFilters, setReportFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const { userRoles } = useContext(DataContext);
  const [drawerTab, setDrawerTab] = useState("columns");
  const [draftFilters, setDraftFilters] = useState(() => new URLSearchParams(filters));
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState("");
  const [isSavedFilterEditOpen, setIsSavedFilterEditOpen] = useState(false);
  const activeDrawerFilters = draftFilters ?? filters;
  const effectiveReportId = reportId ?? reportData?.id ?? reportData?.pk;
  const updateDraftFilter = useCallback(
    (key, value, bulkNewFilters = null, notApplyOffset = false) => {
      const next = bulkNewFilters || new URLSearchParams(activeDrawerFilters);
      updateFilter(key, value, next, notApplyOffset);

      if (!bulkNewFilters) {
        setDraftFilters(new URLSearchParams(next));
      }
    },
    [activeDrawerFilters, updateFilter]
  );
  const retrieveReportFilters = useCallback(async () => {
    setFiltersLoading(true);
    try {
      setReportFilters([]);
      const data = await reportFiltersRepository.query(effectiveReportId);
      setReportFilters(transformFilters(data));
    } finally {
      setFiltersLoading(false);
    }
  }, [effectiveReportId, reportFiltersRepository]);

  useEffect(() => {
    retrieveReportFilters();
  }, [retrieveReportFilters]);

  const bulkUpdateDraftFilters = useCallback(
    (filterMap) => {
      const next = new URLSearchParams(activeDrawerFilters);

      for (const [filterKey, filterValue] of filterMap) {
        updateFilter(filterKey, filterValue, next);
      }

      setDraftFilters(new URLSearchParams(next));
    },
    [activeDrawerFilters, updateFilter]
  );

  const hasPendingDrawerChanges = useMemo(() => {
    const keySet = new Set([...filters.keys(), ...activeDrawerFilters.keys()]);
    for (const key of keySet) {
      if ((filters.get(key) || null) !== (activeDrawerFilters.get(key) || null)) {
        return true;
      }
    }
    return false;
  }, [activeDrawerFilters, filters]);

  const applyFiltersFromSearchParams = useCallback(
    (nextFilters, shouldCloseDrawer = true) => {
      const changes = new Map();
      const keySet = new Set([...filters.keys(), ...nextFilters.keys()]);

      for (const key of keySet) {
        const currentValue = filters.get(key) || null;
        const nextValue = nextFilters.get(key) || null;

        if (currentValue !== nextValue) {
          let processedNextValue = nextValue;
          if (typeof nextValue === "string" && nextValue.includes(",") && key !== "columns") {
            processedNextValue = nextValue.split(",");
          }
          changes.set(key, processedNextValue);
        }
      }

      if (!changes.size) {
        if (shouldCloseDrawer) {
          setDrawerCloseSignal((previous) => previous + 1);
        }
        return;
      }

      onApplyStart();

      bulkUpdateFilters(changes);
      if (shouldCloseDrawer) {
        setDrawerCloseSignal((previous) => previous + 1);
      }
    },
    [bulkUpdateFilters, filters, onApplyStart, setDrawerCloseSignal]
  );

  const applyDraftFilters = useCallback(() => {
    applyFiltersFromSearchParams(activeDrawerFilters, true);
  }, [activeDrawerFilters, applyFiltersFromSearchParams]);

  const applySavedFilterOnLoad = useCallback(
    (nextFilters) => {
      applyFiltersFromSearchParams(nextFilters, false);
    },
    [applyFiltersFromSearchParams]
  );

  const updateSearchFilter = useCallback(
    (key, value, bulkNewFilters = null, notApplyOffset = false) => {
      const currentValue = filters.get(key) || null;
      const nextValue = value || null;

      if (key === "search" && currentValue !== nextValue) {
        onApplyStart();
      }

      updateFilter(key, value, bulkNewFilters, notApplyOffset);
    },
    [filters, onApplyStart, updateFilter]
  );

  const { filterState } = useDrawerFiltersState({
    filterConfig: reportFilters,
    filters: activeDrawerFilters,
  });
  const modifiableFilterFieldNames = useMemo(
    () => filterState.map((filter) => filter.fieldName).filter(Boolean),
    [filterState]
  );

  const clearDraftModifiableFilters = useCallback(() => {
    const next = new URLSearchParams(activeDrawerFilters);
    const keysToDelete = [];

    for (const key of next.keys()) {
      const shouldDelete =
        key === "columns" ||
        modifiableFilterFieldNames.some(
          (fieldName) => key === fieldName || key.startsWith(`${fieldName}__`)
        );

      if (shouldDelete) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => next.delete(key));
    setDraftFilters(next);
  }, [activeDrawerFilters, modifiableFilterFieldNames, setDraftFilters]);

  const hasClearableDraftValues = useMemo(() => {
    for (const key of activeDrawerFilters.keys()) {
      if (key === "columns") {
        return true;
      }

      const isModifiableFieldKey = modifiableFilterFieldNames.some(
        (fieldName) => key === fieldName || key.startsWith(`${fieldName}__`)
      );

      if (isModifiableFieldKey) {
        return true;
      }
    }

    return false;
  }, [activeDrawerFilters, modifiableFilterFieldNames]);

  return (
    <div id="Report-Detail-Header" data-testid="Report-Detail-Header">
      <div className="mb-4 flex flex-wrap justify-between">
        {showTitle && (
          <div className="flex items-center gap-2">
            <Title>{reportData?.displayName}</Title>
            <ReportTooltip description={reportData?.description} />
          </div>
        )}
        <PermissionWrapper
          permission={userRoles.permissions.includes("reports.can_export_reports") && showActions}
        >
          <div className="ml-20 flex-col">
            <ActionsButton reportData={reportData} reportId={effectiveReportId} filters={filters} />
          </div>
        </PermissionWrapper>

        {(showFilters || showSearchBar) && (
          <div className="mt-4 flex w-full gap-2">
            {showSearchBar && (
              <FilterSearchLocal
                sxInput={{ height: "40px" }}
                filters={filters}
                updateFilter={updateSearchFilter}
                debounceMs={750}
              />
            )}
            {showFilters && (
              <DrawerFilters
                drawTittle="Filters"
                triggerId="report-filters-button"
                closeButtonId="report-filters-close-button"
                buttonSx={{ height: "40px" }}
                closeSignal={drawerCloseSignal}
                onOpen={() => {
                  setDrawerTab("filters");
                  if (!draftFilters || Array.from(draftFilters.keys()).length === 0) {
                    setDraftFilters(new URLSearchParams(filters));
                  }
                }}
              >
                <div className="flex h-full min-h-0 flex-col">
                  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    <SavedFiltersSection
                      effectiveReportId={effectiveReportId}
                      filters={filters}
                      activeDrawerFilters={activeDrawerFilters}
                      setDraftFilters={setDraftFilters}
                      modifiableFilterFieldNames={modifiableFilterFieldNames}
                      selectedSavedFilterId={selectedSavedFilterId}
                      setSelectedSavedFilterId={setSelectedSavedFilterId}
                      onEditModeChange={setIsSavedFilterEditOpen}
                      onSaveSuccess={applyDraftFilters}
                      onApplySavedFilterOnLoad={applySavedFilterOnLoad}
                    />
                  </Box>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                    <Tabs
                      value={drawerTab}
                      onChange={(_event, tabValue) => setDrawerTab(tabValue)}
                      aria-label="Report drawer tabs"
                    >
                      <Tab
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>Filters</span>
                            {filtersLoading && <CircularProgress size={14} />}
                          </Box>
                        }
                        value="filters"
                      />
                      <Tab label="Columns" value="columns" />
                    </Tabs>
                  </Box>

                  <div className="min-h-0 flex-1 overflow-hidden">
                    {drawerTab === "columns" ? (
                      <ColumnSelectionTab
                        availableColumns={availableColumns}
                        filters={activeDrawerFilters}
                        updateFilter={updateDraftFilter}
                      />
                    ) : (
                      <DrawerFiltersListLocal
                        filterState={filterState}
                        updateFilter={updateDraftFilter}
                        bulkUpdateFilters={bulkUpdateDraftFilters}
                      />
                    )}
                  </div>

                  <div className="border-t border-gray-200 p-3">
                    <div className="flex gap-2">
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={clearDraftModifiableFilters}
                        disabled={!hasClearableDraftValues || isSavedFilterEditOpen}
                      >
                        Clear
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={applyDraftFilters}
                        disabled={!hasPendingDrawerChanges || isSavedFilterEditOpen}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              </DrawerFilters>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default Header;
